import React, { useCallback, useEffect, useRef, useState } from 'react';

import { getLanguage } from 'helpers/locale';
import Modules from 'root/apps/shared_modules/Modules';
import { datadogLogs } from 'root/datadog/datadogLogs';

import { createMASBaseConfig, MAS_CONFIG } from '../shared/config';
import { useLocale } from '../shared/useLocale';
import { useMASScript } from '../shared/useMASScript';

// Extended configuration for MAS Notification
interface MerchantAccountNotificationConfig {
  baseUrl: string;
  productCode: string;
  entityType: 'subscription' | 'location';
  entityId: string;
  locale: string;
  masUiUrl: string;
  customHeaders?: Record<string, string>;
}

// Custom element type
interface MerchantAccountNotificationElement extends HTMLElement {
  init?: (config: MerchantAccountNotificationConfig) => Promise<void>;
  update?: (config: Partial<MerchantAccountNotificationConfig>) => Promise<void>;
}

// JSX declaration for the custom element
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'merchant-account-notification': React.DetailedHTMLProps<
        React.HTMLAttributes<MerchantAccountNotificationElement>,
        MerchantAccountNotificationElement
      >;
    }
  }
}

const CUSTOM_ELEMENT_NAME = 'merchant-account-notification';

/**
 * MASNotification component renders the MAS notification banner web component.
 * This displays billing-related notifications from the Merchant Account Service.
 *
 * Note: Sidebar/tile notification badges are handled separately by SidebarMASNotificationBadge.
 *
 * Feature flag: subscription.invoice_notification
 */
const MASNotification: React.FC = () => {
  // Check feature flag - return null if not enabled
  const isFeatureEnabled = Modules.hasModule('subscription.invoice_notification');

  const masNotificationRef = useRef<MerchantAccountNotificationElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState(false);
  const locale = useLocale();
  const currentLocale = getLanguage(locale);

  const initNotification = useCallback(() => {
    if (!isFeatureEnabled) return;

    const masNotification = masNotificationRef.current;
    if (!masNotification?.init) return;

    const config: MerchantAccountNotificationConfig = {
      ...createMASBaseConfig(currentLocale),
      masUiUrl: MAS_CONFIG.getMasUiUrl(),
    };

    // In development, add test mode headers to see mock notifications
    if (process.env.NODE_ENV === 'development') {
      config.customHeaders = {
        'x-mas-test-mode': 'true',
        'x-mas-test-notification-button-type': 'payment-failure-single',
      };
    }

    masNotification.addEventListener('merchant-account-notification:error', (event: Event) => {
      const errorDetail = (event as CustomEvent).detail;
      datadogLogs.logger.error('LSR-36587 MASNotification: Error from web component', {
        detail: errorDetail,
        errorMessage: errorDetail?.message,
        entityId: config.entityId,
      });
      setInitError(true);
    });

    masNotification
      .init(config)
      .then(() => {
        datadogLogs.logger.info('LSR-36587 MASNotification: Initialized', {
          entityId: config.entityId,
        });
        setIsInitialized(true);
      })
      .catch((error: Error) => {
        datadogLogs.logger.error('LSR-36587 MASNotification: Init failed', {
          error: error.message,
          entityId: config.entityId,
        });
        setInitError(true);
      });
  }, [currentLocale, isFeatureEnabled]);

  const { hasError: scriptError } = useMASScript(CUSTOM_ELEMENT_NAME, initNotification);

  const hasError = scriptError || initError;

  // Update locale when it changes
  useEffect(() => {
    if (!isInitialized) return;
    masNotificationRef.current?.update?.({ locale: currentLocale });
  }, [currentLocale, isInitialized]);

  if (hasError) return null;

  // Don't render if feature is not enabled
  if (!isFeatureEnabled) return null;

  return (
    <div className="notranslate">
      {/* eslint-disable-next-line react/self-closing-comp */}
      <merchant-account-notification ref={masNotificationRef}></merchant-account-notification>
    </div>
  );
};

export { MASNotification };
